<script lang="ts">
  import type { Fluc } from '$types/races';
  import { formatDateString, formatTime } from '$utils/time';
  import { createFloatingActions } from 'svelte-floating-ui';
  import { offset, shift, flip } from 'svelte-floating-ui/core';

  export let data: Fluc[];
  export let height = 30;
  export let width = 90;
  export let strokeWidth = 2;
  export let strokeColour = null as string | null;
  export let hoverColour = strokeColour;
  export let placement = 'right' as 'right' | 'left' | 'top' | 'bottom';

  let filteredData = [] as Fluc[];
  $: {
    filteredData = data.filter((d) => d.price != 0);

    // if there is only one fluc, duplicate it so the chart doesn't look weird
    if (filteredData.length == 1)
      filteredData = filteredData.concat(filteredData);
  }

  // if strokeColour is not set, calculate it based on the fluc direction
  $: {
    if (!strokeColour) {
      // get the first fluc that isn't zero
      const fluc = data.find((d) => d.price != 0);
      // get the last fluc that isn't zero
      const lastFluc = data.reverse().find((d) => d.price != 0);
      // if the first fluc is less than the last fluc, the price is going down
      if (!fluc?.price || !lastFluc?.price) {
        strokeColour = '#FBBF24';
      } else if (fluc?.price < lastFluc?.price) {
        strokeColour = '#DC2626';
      } else {
        strokeColour = '#059669';
      }
    }
  }

  const [floatingRef, floatingContent] = createFloatingActions({
    strategy: 'absolute',
    placement: placement,
    middleware: [offset(5), flip(), shift()]
  });

  // hoverDisplay returns a <g> element containing a vertical <line> filling the height of the chart
  // and a <circle> positioned at the x and y coordinates of the closest x coordinate to the mouse
  let closest = null as { [x: string]: number } | null;
  let path = `M 0 ${strokeWidth} L 0 ${strokeWidth} L ${width} ${strokeWidth}`;
  let hoveredIndex = -1;
  $: {
    const h = height - strokeWidth * 2;
    const w = width - strokeWidth * 2;

    const prices = filteredData.map((d) => d.price);
    const max = Math.max(...prices);
    const min = Math.min(...prices);

    const range = max - min;
    const totalPoints = (filteredData.length ?? 1) - 1;

    const coordinates = [] as Array<{ [x: string]: number }>;
    prices.forEach((d, i) => {
      // if price is zero, don't add it to the coordinates
      const x = (i / totalPoints) * w + strokeWidth;
      const y = h - ((d - min) / range) * h + strokeWidth;
      if (x && y) coordinates.push({ x, y });
    });

    if (coordinates.length) {
      closest = coordinates?.reduce((prev, curr) => {
        return Math.abs(curr.x - hoverX) < Math.abs(prev.x - hoverX)
          ? curr
          : prev;
      });
    } else {
      closest = null;
    }

    const paths = [] as Array<string>;
    coordinates?.forEach((point) => {
      paths.push(['L', point.x, point.y].join(' '));
    });
    if (coordinates.length > 1)
      path = ['M' + coordinates[0].x, coordinates[0].y, ...paths].join(' ');

    if (closest) {
      hoveredIndex = coordinates.indexOf(closest);
    } else {
      hoveredIndex = -1;
    }
  }

  let hover = false;
  let hoverX = 0;
  function onMouseMove(e: MouseEvent) {
    hoverX = e.offsetX;
    hover = true;
  }

  function onMouseLeave() {
    hover = false;
  }
</script>

<div class="relative" use:floatingRef>
  <svg
    role="img"
    {width}
    {height}
    stroke-width={strokeWidth}
    stroke={strokeColour}
    on:mousemove={onMouseMove}
    on:mouseleave={onMouseLeave}
  >
    <path d={path} fill="none" />
    <g display={hover ? 'block' : 'none'}>
      {#if closest}
        <line
          x1={closest.x}
          x2={closest.x}
          y1="0"
          y2={height}
          stroke={hoverColour}
          stroke-width="1"
          stroke-dasharray="2"
        />
        <circle
          cx={closest.x}
          cy={closest.y}
          r="2"
          fill={hoverColour}
          stroke={hoverColour}
          stroke-width="1"
        />
      {/if}
    </g>
  </svg>

  {#if hover && hoveredIndex > -1}
    <div
      use:floatingContent
      class="absolute z-40 flex w-24 flex-col items-center gap-x-2 rounded-md border border-gray-200 bg-white text-center font-normal shadow-sm shadow-gray-300"
    >
      <div class="flex flex-row place-content-center items-center gap-x-1 p-2">
        <!-- show previous -->
        <span class="w-6 text-[10px] text-gray-300">
          {#if hoveredIndex > 0}
            {filteredData[hoveredIndex - 1].price}
          {/if}
        </span>
        <span class="w-6 font-bold">{filteredData[hoveredIndex].price}</span>

        <!-- show next -->
        <span class="w-6 text-[10px] text-gray-300">
          {#if hoveredIndex < filteredData.length - 1}
            {filteredData[hoveredIndex + 1].price}
          {/if}
        </span>
      </div>
      <!-- show date changed -->
      <div
        class="flex w-full flex-col place-content-center items-center bg-gray-100 p-1"
      >
        <span class="text-[10px] text-gray-500">
          {formatDateString(filteredData[hoveredIndex].lastUpdated)}
        </span>
        <span class="text-[10px] text-gray-700">
          {formatTime(filteredData[hoveredIndex].lastUpdated, true)}
        </span>
      </div>
    </div>
  {/if}
</div>
